import AppColors from 'general/constants/AppColors';
import Utils from 'general/utils/Utils';
import PropTypes from 'prop-types';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import KTBSDropdown, { KTBSDropdownDirections } from '../KTBSDropdown';

const rowsPerPageItemList = [
  {
    name: '5',
    value: '5',
  },
  {
    name: '10',
    value: '10',
  },
  {
    name: '20',
    value: '20',
  },
  {
    name: '30',
    value: '30',
  },
  {
    name: '50',
    value: '50',
  },
  {
    name: '100',
    value: '100',
  },
];

export const KTPaginationColor = {
  primary: 'primary',
  success: 'success',
  warning: 'warning',
  danger: 'danger',
  info: 'info',
  dark: 'dark',
  light: 'light',
};

KTPagination.propTypes = {
  rowsPerPage: PropTypes.number,
  rowCount: PropTypes.number,
  currentPage: PropTypes.number,
  onChangePage: PropTypes.func,
  onChangeRowsPerPage: PropTypes.func,
  loading: PropTypes.bool,
  color: PropTypes.oneOf(Object.values(KTPaginationColor)),
  circleStyle: PropTypes.bool,
  hidePerPageBlock: PropTypes.bool,
  currentItemInPageCount: PropTypes.number,
  appendText: PropTypes.string,
};

function KTPagination({
  rowsPerPage = 20,
  rowCount = 0,
  currentPage = 1,
  onChangePage = null,
  onChangeRowsPerPage = null,
  loading = false,
  color = KTPaginationColor.light,
  circleStyle = false,
  hidePerPageBlock = false,
  currentItemInPageCount = 0,
  appendText = '',
}) {
  // MARK: --- Params ---
  const { t } = useTranslation();
  const totalPages = Math.ceil(rowCount / rowsPerPage);
  const iDisplayFrom = (currentPage - 1) * rowsPerPage + 1;
  let iDisplayTo = (currentPage - 1) * rowsPerPage + rowsPerPage;
  if (iDisplayTo > rowCount) {
    iDisplayTo = rowCount;
  }
  let firstIndex =
    currentPage - 3 > 0 ? (currentPage - 3 > totalPages - 5 ? totalPages - 5 : currentPage - 3) : 0;
  if (firstIndex < 0) firstIndex = 0;
  let lastIndex =
    currentPage + 1 > totalPages - 1 ? totalPages - 1 : currentPage + 1 < 4 ? 4 : currentPage + 1;
  if (lastIndex > totalPages - 1) {
    lastIndex = totalPages - 1;
  }

  const handlePageChange = useCallback(
    (e) => {
      const newPage = e?.target?.getAttribute('page');
      const iNewPage = parseInt(newPage);
      if (onChangePage && iNewPage !== currentPage) {
        onChangePage(newPage);
      }
    },
    [currentPage, onChangePage]
  );

  const handlePerPageChange = useCallback((e) => {
    const value = e?.target?.innerText;
    const intValue = parseInt(value);
    if (onChangeRowsPerPage && intValue) {
      onChangeRowsPerPage(intValue);
    }
  });

  const arrButtons = useMemo(() => {
    const arrButtons = [];
    for (firstIndex; firstIndex <= lastIndex; firstIndex++) {
      arrButtons.push(
        <div
          key={firstIndex}
          page={firstIndex + 1}
          onClick={handlePageChange}
          className={`btn btn-icon btn-sm border-0 ${circleStyle ? 'btn-circle' : ''} ${
            color === KTPaginationColor.light ? 'btn-light' : 'btn-light'
          } mr-2 my-1 ${
            firstIndex === currentPage - 1 ? 'active btn-hover-' + color : ''
          } btn-hover-${color}`}
        >
          {Utils.formatNumber(firstIndex + 1)}
        </div>
      );
    }
    return arrButtons;
  }, [firstIndex, lastIndex, currentPage, handlePageChange]);

  return (
    <div className="">
      <div className="d-flex justify-content-between align-items-center flex-wrap">
        {/* block 1 */}
        <div className="d-flex flex-wrap mr-3">
          {/* first button */}
          <div
            className={`btn btn-icon btn-sm ${circleStyle ? 'btn-circle' : ''} ${
              color === KTPaginationColor.light ? 'btn-light' : 'btn-light'
            } mr-2 my-1 ${currentPage <= 1 ? 'disabled' : ''} btn-hover-${color}`}
            onClick={() => {
              onChangePage(1);
            }}
          >
            <i className="ki ki-double-arrow-back icon-xs" />
          </div>
          {/* prev button */}
          <div
            className={`btn btn-icon btn-sm ${circleStyle ? 'btn-circle' : ''} ${
              color === KTPaginationColor.light ? 'btn-light' : 'btn-light'
            } mr-2 my-1 ${currentPage <= 1 ? 'disabled' : ''} btn-hover-${color}`}
            onClick={() => {
              onChangePage(currentPage - 1);
            }}
          >
            <i className="ki ki-arrow-back icon-xs" />
          </div>
          {arrButtons}
          {/* next button */}
          <div
            className={`btn btn-icon btn-sm ${circleStyle ? 'btn-circle' : ''} ${
              color === KTPaginationColor.light ? 'btn-light' : 'btn-light'
            } mr-2 my-1 ${currentPage >= totalPages ? 'disabled' : ''} btn-hover-${color}`}
            onClick={() => {
              onChangePage(currentPage + 1);
            }}
          >
            <i className="ki ki-arrow-next icon-xs" />
          </div>
          {/* last button */}
          <div
            className={`btn btn-icon btn-sm ${circleStyle ? 'btn-circle' : ''} ${
              color === KTPaginationColor.light ? 'btn-light' : 'btn-light'
            } mr-2 my-1 ${currentPage >= totalPages ? 'disabled' : ''} btn-hover-${color}`}
            onClick={() => {
              onChangePage(totalPages);
            }}
          >
            <i className="ki ki-double-arrow-next icon-xs" />
          </div>
        </div>
        {/* block 2 */}
        {!hidePerPageBlock && (
          <div className="d-flex align-items-center">
            <div className="d-flex align-items-center">
              {/* loading */}
              {loading && (
                <div className="d-flex align-items-center">
                  <div className="mr-2 text-dark-75">{`${t('loading')}...`}</div>
                  <div
                    className={`spinner ${
                      color !== KTPaginationColor.light ? 'spinner-' + color : ''
                    } mr-10`}
                  ></div>
                </div>
              )}
              {/* select per page */}
              <KTBSDropdown
                toggleElement={
                  <div
                    className={`d-flex align-items-center btn btn-light dropdown-toggle btn-sm btn-${color}`}
                  >
                    {rowsPerPage}
                  </div>
                }
                direction={KTBSDropdownDirections.up}
                dropdownMenuItems={rowsPerPageItemList}
                selectedValue={`${rowsPerPage}`}
                onChange={(newValue) => {
                  if (onChangeRowsPerPage) {
                    onChangeRowsPerPage(newValue);
                  }
                }}
              />
              <span
                className="text-dark-75 ml-4"
                style={{ fontWeight: 400, fontSize: '14px', color: AppColors.gray[600] }}
              >
                Hiển thị {rowsPerPage * (currentPage - 1) + currentItemInPageCount}/
                <span style={{ fontWeight: 600, fontSize: '14px', color: AppColors.primary }}>
                  {Utils.formatNumber(rowCount)} {appendText}
                </span>
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default KTPagination;
